import { useState } from "react";
import { Navigate } from "react-router-dom";
import {
  CreateCustomerResult,
  CustomerRecordKind,
  CustomerRow,
  useCustomerList,
} from "../api/platformCustomers";
import { useAuth } from "../auth/AuthContext";
import { CreateCustomerModal } from "../components/customers/CreateCustomerModal";
import { CustomerRecordsModal } from "../components/customers/CustomerRecordsModal";
import { EditCustomerModal } from "../components/customers/EditCustomerModal";
import { ResetCustomerPasswordModal } from "../components/customers/ResetCustomerPasswordModal";
import { UI_LOADING } from "../constants/uiLabels";
import "./CustomersPage.css";

function CustomerStatButton(props: {
  count: number;
  label: string;
  onOpen: () => void;
}) {
  const { count, label, onOpen } = props;
  if (count <= 0) {
    return (
      <span className="kp-customers__stat kp-customers__stat--empty" title={label}>
        <strong>0</strong> {label}
      </span>
    );
  }
  return (
    <button type="button" className="kp-customers__stat-link" onClick={onOpen} title={`${label} listesi`}>
      <strong>{count}</strong> {label}
    </button>
  );
}

export function CustomersPage() {
  const { session } = useAuth();
  const isPlatformOwner = session?.user?.is_platform_owner === true;
  const list = useCustomerList();
  const [showCreate, setShowCreate] = useState(false);
  const [editTarget, setEditTarget] = useState<CustomerRow | null>(null);
  const [resetTarget, setResetTarget] = useState<CustomerRow | null>(null);
  const [recordsTarget, setRecordsTarget] = useState<{
    customer: CustomerRow;
    kind: CustomerRecordKind;
  } | null>(null);
  const [lastCreated, setLastCreated] = useState<CreateCustomerResult | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  if (!isPlatformOwner) {
    return <Navigate to="/dashboard" replace />;
  }

  const rows = list.data?.rows || [];
  const total = list.data?.total ?? 0;

  return (
    <div className="kp-customers">
      <div className="kp-customers__header">
        <div>
          <h1>Müşteriler</h1>
          <p>
            Satış yaptığınız her lokasyon için ayrı kurum açın. Toplam {total} müşteri.
            Kayıt sayıları salt okunur; tıklayınca o kurumun listesini görürsünüz.
          </p>
        </div>
        <div className="kp-customers__header-actions">
          <button className="kp-btn" onClick={list.reload} disabled={list.loading}>
            Yenile
          </button>
          <button className="kp-btn kp-btn--primary" onClick={() => setShowCreate(true)}>
            + Yeni Müşteri
          </button>
        </div>
      </div>

      {lastCreated && (
        <div className="kp-card kp-customers__success" role="status">
          <strong>Müşteri oluşturuldu</strong>
          <p>{lastCreated.login_hint}</p>
          <p>
            Yönetici: {lastCreated.admin_name} · {lastCreated.admin_email}
          </p>
          <button type="button" className="kp-btn kp-btn--ghost" onClick={() => setLastCreated(null)}>
            Kapat
          </button>
        </div>
      )}

      {flash && (
        <div className="kp-card kp-customers__success" role="status">
          <p>{flash}</p>
          <button type="button" className="kp-btn kp-btn--ghost" onClick={() => setFlash(null)}>
            Kapat
          </button>
        </div>
      )}

      {list.error && <div className="kp-customers__error">{list.error}</div>}

      <div className="kp-card kp-customers__table-wrap">
        {list.loading && rows.length === 0 ? (
          <p className="kp-muted">{UI_LOADING}</p>
        ) : rows.length === 0 ? (
          <p className="kp-muted">Henüz müşteri yok. + Yeni Müşteri ile ilk kaydı oluşturun.</p>
        ) : (
          <table className="kp-customers__table">
            <thead>
              <tr>
                <th>Kurum (Kurs Adı)</th>
                <th>Yönetici</th>
                <th>Kayıtlar</th>
                <th>Durum</th>
                <th>Oluşturma</th>
                <th>İşlem</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>
                    <strong>{row.name}</strong>
                  </td>
                  <td>
                    <div>{row.admin_name || "—"}</div>
                    <div className="kp-muted">{row.admin_email || ""}</div>
                  </td>
                  <td>
                    <div className="kp-customers__stats">
                      <CustomerStatButton
                        count={row.total_students ?? 0}
                        label="kursiyer"
                        onOpen={() => setRecordsTarget({ customer: row, kind: "students" })}
                      />
                      <CustomerStatButton
                        count={row.total_instructors ?? 0}
                        label="eğitmen"
                        onOpen={() => setRecordsTarget({ customer: row, kind: "instructors" })}
                      />
                      <CustomerStatButton
                        count={row.total_vehicles ?? 0}
                        label="araç"
                        onOpen={() => setRecordsTarget({ customer: row, kind: "vehicles" })}
                      />
                      <CustomerStatButton
                        count={row.total_users ?? 0}
                        label="kullanıcı"
                        onOpen={() => setRecordsTarget({ customer: row, kind: "users" })}
                      />
                    </div>
                  </td>
                  <td>
                    <span className={row.is_active ? "kp-customers__ok" : "kp-muted"}>
                      {row.is_active ? "Aktif" : "Pasif"}
                    </span>
                  </td>
                  <td className="kp-muted">
                    {row.created_at ? new Date(row.created_at).toLocaleString("tr-TR") : "—"}
                  </td>
                  <td>
                    <div className="kp-customers__row-actions">
                      <button
                        type="button"
                        className="kp-btn kp-btn--ghost kp-customers__action-btn"
                        onClick={() => {
                          setFlash(null);
                          setEditTarget(row);
                        }}
                      >
                        Düzenle
                      </button>
                      <button
                        type="button"
                        className="kp-btn kp-btn--ghost kp-customers__action-btn"
                        disabled={!row.admin_user_id}
                        title={
                          row.admin_user_id
                            ? "Yönetici parolasını sıfırla"
                            : "Aktif yönetici yok"
                        }
                        onClick={() => {
                          setFlash(null);
                          setResetTarget(row);
                        }}
                      >
                        Parola Sıfırla
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showCreate && (
        <CreateCustomerModal
          onClose={() => setShowCreate(false)}
          onCreated={(result) => {
            setShowCreate(false);
            setLastCreated(result);
            void list.reload();
          }}
        />
      )}

      {editTarget && (
        <EditCustomerModal
          customer={editTarget}
          onClose={() => setEditTarget(null)}
          onUpdated={(result) => {
            setEditTarget(null);
            setFlash(`Güncellendi: ${result.login_hint}`);
            void list.reload();
          }}
        />
      )}

      {resetTarget && (
        <ResetCustomerPasswordModal
          customer={resetTarget}
          onClose={() => setResetTarget(null)}
          onDone={(result) => {
            setFlash(`Parola sıfırlandı. ${result.login_hint}`);
          }}
        />
      )}

      {recordsTarget && (
        <CustomerRecordsModal
          customer={recordsTarget.customer}
          initialKind={recordsTarget.kind}
          onClose={() => setRecordsTarget(null)}
        />
      )}
    </div>
  );
}
